import React, { useState } from 'react';
import { NavLink, useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import {
  LayoutDashboard, ShoppingCart, Users, Package, MapPin, Truck,
  BarChart3, Building2, Settings, ChevronDown, ChevronRight,
  LogOut, Wheat, Bell, Menu, X, Tag
} from 'lucide-react';

const NavItem = ({ to, icon: Icon, label, exact = false, badge = 0, onBadgeClear }) => (
  <NavLink
    to={to}
    end={exact}
    onClick={() => badge > 0 && onBadgeClear?.()}
    className={({ isActive }) =>
      `flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm transition-colors duration-150 font-dashboard ${
        isActive
          ? 'bg-brand-accent-light text-brand-dark font-semibold'
          : 'text-brand-text-muted hover:bg-brand-bg hover:text-brand-text'
      }`
    }
    data-testid={`nav-${label.toLowerCase().replace(/\s/g, '-')}`}
  >
    <Icon className="w-4 h-4 flex-shrink-0" strokeWidth={1.8} />
    <span className="flex-1">{label}</span>
    {badge > 0 && (
      <span className="min-w-[18px] h-[18px] px-1 bg-red-500 text-white text-[10px] font-bold rounded-full flex items-center justify-center animate-pulse" data-testid="pedidos-badge">
        {badge > 99 ? '99+' : badge}
      </span>
    )}
  </NavLink>
);

const NavGroup = ({ label, icon: Icon, children }) => {
  const [open, setOpen] = useState(true);
  return (
    <div>
      <button
        onClick={() => setOpen(!open)}
        className="flex items-center gap-3 px-3 py-2 w-full text-xs uppercase tracking-widest text-brand-text-muted font-dashboard font-semibold"
      >
        <span className="flex-1 text-left">{label}</span>
        {open ? <ChevronDown className="w-3 h-3" /> : <ChevronRight className="w-3 h-3" />}
      </button>
      {open && <div className="space-y-0.5 mt-1">{children}</div>}
    </div>
  );
};

export default function Sidebar({ collapsed, onToggle, newOrdersCount = 0, onClearOrders }) {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const isAdmin = user?.role === 'admin';

  const handleLogout = async () => {
    await logout();
    navigate('/admin/login');
  };

  return (
    <aside
      className={`dashboard-sidebar fixed top-0 left-0 h-screen bg-white border-r border-brand-border flex flex-col z-30 transition-all duration-200 ${
        collapsed ? 'w-0 overflow-hidden' : 'w-64'
      }`}
      data-testid="dashboard-sidebar"
    >
      {/* Logo */}
      <div className="flex items-center gap-3 px-4 py-3 border-b border-brand-border">
        <img src="/logo-fatima.jpg" alt="Fátima Guterres" className="h-10 w-auto rounded-lg object-cover flex-shrink-0" />
        <p className="text-xs text-brand-text-muted font-body capitalize">{user?.role}</p>
      </div>

      {/* Nav */}
      <nav className="flex-1 overflow-y-auto px-3 py-4 space-y-4">
        <NavItem to="/admin/dashboard" icon={LayoutDashboard} label="Dashboard" exact />

        <NavGroup label="Operação">
          <NavItem to="/admin/pedidos" icon={ShoppingCart} label="Pedidos" badge={newOrdersCount} onBadgeClear={onClearOrders} />
          <NavItem to="/admin/clientes" icon={Users} label="Clientes" />
        </NavGroup>

        <NavGroup label="Cadastros">
          <NavItem to="/admin/produtos" icon={Package} label="Produtos" />
          <NavItem to="/admin/categorias" icon={Tag} label="Categorias" />
          <NavItem to="/admin/configuracoes/formas-entrega" icon={Settings} label="Formas de Entrega" />
          <NavItem to="/admin/transportadoras" icon={Truck} label="Transportadoras" />
          <NavItem to="/admin/rotas" icon={MapPin} label="Rotas" />
        </NavGroup>

        <NavGroup label="Relatórios">
          <NavItem to="/admin/relatorios/producao" icon={BarChart3} label="Produção" />
          <NavItem to="/admin/relatorios/romaneio" icon={MapPin} label="Romaneio" />
          {isAdmin && <NavItem to="/admin/relatorios/gestao" icon={BarChart3} label="Gestão" />}
        </NavGroup>

        {isAdmin && (
          <NavGroup label="Sistema">
            <NavItem to="/admin/empresa" icon={Building2} label="Empresa" />
            <NavItem to="/admin/configuracoes/tipos-cliente" icon={Settings} label="Tipos de Cliente" />
            <NavItem to="/admin/configuracoes/usuarios" icon={Users} label="Usuários" />
          </NavGroup>
        )}
      </nav>

      {/* User info */}
      <div className="border-t border-brand-border p-3">
        <div className="flex items-center gap-3 px-2 py-2">
          <div className="w-8 h-8 bg-brand-accent-mid rounded-full flex items-center justify-center flex-shrink-0">
            <span className="text-brand-dark font-bold text-sm font-body">{user?.nome?.[0]?.toUpperCase()}</span>
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-sm font-semibold text-brand-text font-dashboard truncate">{user?.nome}</p>
            <p className="text-xs text-brand-text-muted truncate">{user?.email}</p>
          </div>
          <button onClick={handleLogout} className="p-1.5 hover:bg-red-50 rounded-lg text-brand-text-muted hover:text-red-600 transition-colors" title="Sair" data-testid="logout-btn">
            <LogOut className="w-4 h-4" />
          </button>
        </div>
      </div>
    </aside>
  );
}
